// アイテムのオプション選択に関する関数群
import { PRODUCT_TYPE } from '@/constants';
import type { GfProductColor, GfProductDetailForDesign, GfProductSize } from '@/types';

/** カート・購入・履歴・注文照会などで共通に使うオプション表示行 */
export type ProductOptionAttribute = { label: string; value: string };

export type ProductOptionDisplay = {
  /** スペック並びで使う {label, value} の配列。テンプレはカラー1行のみ、非テンプレはカラー・サイズ2行 */
  attributes: ProductOptionAttribute[];
  /** 基本料金の右に「（〜）」で出す1行ラベル。テンプレはカラー名、非テンプレは「カラー / Mサイズ」形式 */
  basePriceLabel: string;
};

/**
 * カート・購入・履歴・注文照会で表示するオプション情報を組み立てる
 *
 * 非テンプレートはカラー・サイズ両方
 * テンプレートはカラーのみ
 */
export const buildProductOptionDisplay = ({
  productType,
  colorName,
  sizeName,
}: {
  productType?: string | null;
  colorName: string;
  sizeName: string;
}): ProductOptionDisplay => {
  if (productType === PRODUCT_TYPE.template) {
    return {
      attributes: colorName ? [{ label: 'カラー', value: colorName }] : [],
      basePriceLabel: colorName,
    };
  }
  return {
    attributes: [
      { label: 'カラー', value: colorName },
      { label: 'サイズ', value: sizeName },
    ],
    basePriceLabel: `${colorName} / ${sizeName}サイズ`,
  };
};

/**
 * 選択中のoptionのオブジェクトを返す
 * @param productDetail 商品の詳細データ
 * @param colorId 選択したカラーID
 * @param sizeId 選択したサイズID
 * @returns 選択中のオプション
 */
export const filterSelectedOptions = (
  productDetail: GfProductDetailForDesign,
  colorId: number,
  sizeId: number,
): {
  color?: GfProductColor;
  size?: GfProductSize;
} => {
  const selectColor = productDetail.variations.colors.find((color) => color.id === colorId);
  const selectSize = productDetail.variations.sizes.find((size) => size.id === sizeId);
  return {
    color: selectColor,
    size: selectSize,
  };
};

/**
 * 選択中オプションのテキストを作成
 * @param color 選択したカラー
 * @param size 選択したサイズ
 * @returns 選択中オプションのテキスト
 */
export const buildSelectedOptionsText = ({
  color,
  size,
}: {
  color?: GfProductColor;
  size?: GfProductSize;
}) => {
  const result: string[] = [];
  if (color) result.push(`${color.name}`);
  if (size) result.push(`${size.name}サイズ`);

  return `（${result.join(' / ')}）`;
};
